import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  Link2,
  BookOpen,
  Award,
  Briefcase,
  FlaskConical,
  Beaker,
  Database,
  AlertTriangle,
  Menu,
  Images,
  ContactRound,
  Inbox,
  MessageSquareWarning,
  LayoutDashboard,
  Users,
  Palette,
  Home,
  BarChart3,
  Calculator,
  GraduationCap,
  Shield,
} from "lucide-react";
import { functions } from "../firebase/config";
import { useCmsAuth } from "../context/CmsAuthContext";
import CmsGoLivePanel from "./CmsGoLivePanel";

const ALL_SECTIONS = [
  {
    to: "/admin",
    title: "Resumen",
    desc: "Accesos rápidos según sus módulos.",
    icon: LayoutDashboard,
    module: "dashboard",
    hideOnDashboard: true,
  },
  {
    to: "/admin/home",
    title: "Home",
    desc: "Centro de inteligencia y tarjetas de servicio de la portada.",
    icon: Home,
    module: "home",
  },
  {
    to: "/admin/clientes",
    title: "Clientes",
    desc: "Fichas comerciales asignadas a cada vendedora.",
    icon: ContactRound,
    module: "clientes",
  },
  {
    to: "/admin/leads",
    title: "Consultas y cotizaciones",
    desc: "Leads del sitio web vinculados a clientes.",
    icon: Inbox,
    module: "leads",
  },
  {
    to: "/admin/quejas",
    title: "Quejas / sugerencias",
    desc: "Buzón LABSYSO y seguimiento F-PGC-6-2.",
    icon: MessageSquareWarning,
    module: "quejas",
  },
  {
    to: "/admin/footer",
    title: "Footer",
    desc: "Redes sociales y enlaces del pie de página.",
    icon: Link2,
    module: "footer",
  },
  {
    to: "/admin/navigation",
    title: "Menú responsivo",
    desc: "Iconos del menú hamburguesa y de la barra inferior en móvil.",
    icon: Menu,
    module: "navigation",
  },
  {
    to: "/admin/about",
    title: "Nosotros",
    desc: "Misión, visión, valores, liderazgo e identidad.",
    icon: BookOpen,
    module: "about",
  },
  {
    to: "/admin/credibility",
    title: "Credibilidad",
    desc: "Proveedores (marcas), testimonios y acreditaciones con logos.",
    icon: Award,
    module: "credibility",
  },
  {
    to: "/admin/services",
    title: "Servicios",
    desc: "Categorías, servicios y productos.",
    icon: Briefcase,
    module: "services",
  },
  {
    to: "/admin/research",
    title: "Investigación",
    desc: "Publicaciones e investigadores.",
    icon: FlaskConical,
    module: "research",
  },
  {
    to: "/admin/labsyso",
    title: "LABSYSO",
    desc: "Exámenes, categorías y contenido del laboratorio.",
    icon: Beaker,
    module: "labsyso",
  },
  {
    to: "/admin/identidad",
    title: "Identidad",
    desc: "Marca, HERO y Medios (videos del fondo).",
    icon: Palette,
    module: "identidad",
  },
  {
    to: "/admin/icons",
    title: "Iconos",
    desc: "Catálogo, presets, marca y archivos subidos.",
    icon: Images,
    module: "icons",
  },
  {
    to: "/admin/calculadoras",
    title: "Calculadoras",
    desc: "Títulos, protocolos y diagnósticos técnicos SSO.",
    icon: Calculator,
    module: "calculadoras",
  },
  {
    to: "/admin/privacidad",
    title: "Privacidad",
    desc: "Aviso de privacidad, cookies y matriz con/sin consentimiento.",
    icon: Shield,
    module: "privacidad",
  },
  {
    to: "/admin/informes",
    title: "Informes",
    desc: "Tráfico AWStats y proyección de costo Firestore.",
    icon: BarChart3,
    module: "informes",
  },
  {
    to: "/admin/users",
    title: "Usuarios",
    desc: "Roles y módulos de acceso al CMS.",
    icon: Users,
    module: "users",
  },
  {
    to: "/admin/biblioteca/usuario",
    title: "Tutorial",
    desc: "Manuales de usuario, producto y técnico empaquetados en el panel.",
    icon: GraduationCap,
    module: null,
  },
];

export default function CmsDashboardPage() {
  const { profile, hasModule, isVendedor, isCalidad } = useCmsAuth();
  const isOwner = profile?.role === "owner";
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState("");
  const [seedErr, setSeedErr] = useState("");

  const sections = useMemo(
    () =>
      ALL_SECTIONS.filter(
        (s) => !s.hideOnDashboard && (s.module == null || hasModule(s.module))
      ),
    [hasModule]
  );

  const title = isVendedor
    ? "Panel comercial"
    : isCalidad
      ? "Panel de calidad"
      : "Panel de mantenimiento";

  const subtitle = isVendedor
    ? "Consulte sus clientes y las consultas/cotizaciones asignadas desde el sitio web."
    : isCalidad
      ? "Gestione el buzón de quejas y sugerencias LABSYSO y su seguimiento."
      : "Edite secciones del sitio y módulos autorizados según su rol.";

  const onSeedContent = async () => {
    if (
      !window.confirm(
        "Esto sobrescribe (merge) el contenido CMS en Firestore con los JSON embebidos en Cloud Functions. ¿Continuar?"
      )
    ) {
      return;
    }
    setSeeding(true);
    setSeedMsg("");
    setSeedErr("");
    try {
      const fn = httpsCallable(functions, "seedCmsContent");
      await fn({});
      setSeedMsg("Contenido resembrado en Firestore.");
    } catch (err) {
      setSeedErr(err?.message || "No se pudo reseeding.");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-3xl font-black text-brand-dark">{title}</h2>
        <p className="text-brand-dark/60 mt-2 max-w-2xl">{subtitle}</p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {sections.map(({ to, title: t, desc, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="group p-6 bg-white rounded-[28px] border border-brand-soft hover:border-brand-primary/30 hover:shadow-lg transition-all"
          >
            <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 text-brand-primary flex items-center justify-center mb-4 group-hover:bg-brand-primary group-hover:text-white transition-colors">
              <Icon size={20} />
            </div>
            <h3 className="font-display font-black text-xl text-brand-dark">{t}</h3>
            <p className="text-sm text-brand-dark/60 mt-2 leading-relaxed">{desc}</p>
          </Link>
        ))}
      </div>

      {isOwner && <CmsGoLivePanel />}

      {isOwner && (
        <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4 max-w-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-dark text-brand-accent flex items-center justify-center shrink-0">
              <Database size={18} />
            </div>
            <div>
              <h3 className="font-display font-black text-xl">Resembrar contenido</h3>
              <p className="text-sm text-brand-dark/60 mt-1 leading-relaxed">
                Solo owner. Empuja de nuevo los JSON del paquete{" "}
                <code className="text-xs">functions/seed</code>.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2 text-xs text-brand-dark/55 bg-[#f8faf9] rounded-xl px-3 py-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-600" />
            No afecta usuarios, quejas ni sesiones OTP.
          </div>
          {seedErr && <p className="text-sm text-red-600">{seedErr}</p>}
          {seedMsg && <p className="text-sm text-brand-primary font-medium">{seedMsg}</p>}
          <button
            type="button"
            onClick={onSeedContent}
            disabled={seeding}
            className="bg-brand-dark text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest disabled:opacity-60"
          >
            {seeding ? "Resembrando…" : "Resembrar desde Functions"}
          </button>
        </section>
      )}
    </div>
  );
}
